/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { captureReferralCode } from '@shared/utils/referral-storage';

interface ReferralCaptureProviderProps {
  children: ReactNode;
}

/**
 * Captures `?ref=` synchronously during render (not in an effect) so referral is persisted
 * before any child effect — such as StepsProvider's URL rewrite — can strip the param.
 *
 * Re-runs whenever `location.search` changes (SPA navigation, OAuth return, etc.).
 * Regression coverage:
 * `src/features/early-access/__test__/referral-capture-spa-navigation.integration.test.tsx`.
 */
export const ReferralCaptureProvider: React.FC<ReferralCaptureProviderProps> = ({ children }) => {
  const { search } = useLocation();
  const lastCapturedSearch = useRef<string | undefined>(undefined);

  const effectiveSearch = search || (typeof window !== 'undefined' ? window.location.search : '');

  if (effectiveSearch !== lastCapturedSearch.current) {
    lastCapturedSearch.current = effectiveSearch;
    try {
      captureReferralCode(effectiveSearch);
    } catch {
      // silently ignore -- referral capture must never block the app
    }
  }

  return <>{children}</>;
};
